import type { LenderYields } from '../../../../types/lenderTypes'

/**
 * Organic yield after depositing `amountUSD`.
 * Derived from getOrganicYieldOpen with amountInUSD=0.
 */
export function getOrganicYieldDeposit(
  amountUSD: number,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldParams: LenderYields
): [borrowApr: number, depositApr: number, apr: number] {
  const totalCashFlowDeposits = cashFlowDeposits + amountUSD * yieldParams.depositRate
  const newCollat = deposits + amountUSD
  return [
    Math.abs(debt) < 0.01 ? 0 : cashFlowDebt / debt,
    totalCashFlowDeposits / newCollat,
    (totalCashFlowDeposits - cashFlowDebt) / (newCollat - debt),
  ]
}

/**
 * Organic yield after borrowing `amountUSD`.
 * Derived from getOrganicYieldOpen with amountOutUSD=0.
 */
export function getOrganicYieldBorrow(
  amountUSD: number,
  irMode: number,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldParams: LenderYields
): [borrowApr: number, depositApr: number, apr: number] {
  const totalCashFlowDebt =
    cashFlowDebt +
    amountUSD *
      (irMode === 1 ? yieldParams.stableBorrowRate : yieldParams.variableBorrowRate)
  const newDebt = debt + amountUSD
  return [
    totalCashFlowDebt / newDebt,
    Math.abs(deposits) < 0.01 ? 0 : cashFlowDeposits / deposits,
    (cashFlowDeposits - totalCashFlowDebt) / (deposits - newDebt),
  ]
}

/**
 * Organic yield after withdrawing `amountUSD`.
 * Derived from getOrganicYieldClose with amountOutUSD=0.
 */
export function getOrganicYieldWithdraw(
  amountUSD: number,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldParams: LenderYields
): [borrowApr: number, depositApr: number, apr: number] {
  const totalCashFlowDeposits = cashFlowDeposits - amountUSD * yieldParams.depositRate
  const newCollat = deposits - amountUSD
  return [
    Math.abs(debt) < 0.01 ? 0 : cashFlowDebt / debt,
    totalCashFlowDeposits / newCollat,
    (totalCashFlowDeposits - cashFlowDebt) / (newCollat - debt),
  ]
}

/**
 * Organic yield after repaying `amountUSD`.
 * Derived from getOrganicYieldClose with amountInUSD=0.
 */
export function getOrganicYieldRepay(
  amountUSD: number,
  irMode: number,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldParams: LenderYields
): [borrowApr: number, depositApr: number, apr: number] {
  const totalCashFlowDebt =
    cashFlowDebt -
    amountUSD *
      (irMode === 1 ? yieldParams.stableBorrowRate : yieldParams.variableBorrowRate)
  const newDebt = debt - amountUSD
  return [
    Math.abs(newDebt) < 0.01 ? 0 : totalCashFlowDebt / newDebt,
    Math.abs(deposits) < 0.01 ? 0 : cashFlowDeposits / deposits,
    (cashFlowDeposits - totalCashFlowDebt) / (deposits - newDebt),
  ]
}
